import React from 'react';
import { isNil } from 'lodash';

import IconButton from '~/components/core/Atomic/Buttons/IconButton';
import type { DndDragHandleProps } from '~/components/core/DndOrderedList/DndOrderedList.types';
import Text from '~/components/core/TextComponents/Text';
import { PencilIcon } from '~/components/icons';
import useOrganization from '~/components/OrganizationContext';
import OverflowTextWithToolTip from '~/components/OverflowTextWithToolTip';
import ReorderDragHandle from '~/components/SystemConfiguration/FnolConfiguration/FnolConfigurationScreen/components/ReorderDragHandle';
import Switch from '~/components/ui/atoms/Switch';
import type { ClaimMilestoneDefinitionModel } from '~/server_shared/openapi/data-contracts';

import { useScopeSummary } from '../../ScopeFields/useScopeSummary';
import TEXTS from '../texts';

// Shared column template so the header row and every data row line up. Columns:
// drag handle · name · key · [sub-orgs] · lines of business · conditional · active · actions.
// The sub-orgs column only exists when the org has sub-organizations enabled.
export const milestoneRowGrid = (showSubOrgs: boolean) =>
  showSubOrgs
    ? 'grid grid-cols-[32px_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1.25fr)_minmax(0,1.25fr)_96px_88px_72px] items-center gap-x-12'
    : 'grid grid-cols-[32px_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1.25fr)_96px_88px_72px] items-center gap-x-12';

interface ClaimMilestoneRowProps {
  milestone: ClaimMilestoneDefinitionModel;
  dragHandleProps: DndDragHandleProps;
  onToggle: () => void;
  onEdit: () => void;
  disabled: boolean;
}

const ClaimMilestoneRow: React.FC<ClaimMilestoneRowProps> = ({
  milestone,
  dragHandleProps,
  onToggle,
  onEdit,
  disabled,
}) => {
  const { key, label, is_enabled, conditions_json } = milestone;
  const { subOrganizationEnabled } = useOrganization() as { subOrganizationEnabled?: boolean };
  const { subOrgSummary, lobSummary } = useScopeSummary(milestone);

  return (
    <div className={`${milestoneRowGrid(!!subOrganizationEnabled)} rounded-lg bg-slate-200 px-8 py-12`}>
      <ReorderDragHandle dragHandleProps={dragHandleProps} disabled={disabled} iconSize={24} />
      <OverflowTextWithToolTip tooltipTitle={label}>
        <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.SEMI_BOLD} className="inline">
          {label}
        </Text>
      </OverflowTextWithToolTip>
      <OverflowTextWithToolTip tooltipTitle={key}>
        <Text variant={Text.VARIANTS.SM} className="inline">
          {key}
        </Text>
      </OverflowTextWithToolTip>
      {subOrganizationEnabled ? (
        <OverflowTextWithToolTip tooltipTitle={subOrgSummary}>
          <Text variant={Text.VARIANTS.SM} colorVariant={Text.COLOR_VARIANTS.SECONDARY} className="inline">
            {subOrgSummary}
          </Text>
        </OverflowTextWithToolTip>
      ) : null}
      <OverflowTextWithToolTip tooltipTitle={lobSummary}>
        <Text variant={Text.VARIANTS.SM} colorVariant={Text.COLOR_VARIANTS.SECONDARY} className="inline">
          {lobSummary}
        </Text>
      </OverflowTextWithToolTip>
      <Text variant={Text.VARIANTS.SM} colorVariant={Text.COLOR_VARIANTS.SECONDARY}>
        {isNil(conditions_json) ? TEXTS.conditional.no : TEXTS.conditional.yes}
      </Text>
      <Switch
        checked={is_enabled}
        onChange={onToggle}
        disabled={disabled}
        aria-label={`${TEXTS.columns.active} — ${label}`}
        className="justify-self-end"
      />
      <IconButton onClick={onEdit} disabled={disabled} aria-label={TEXTS.editMilestone} className="justify-self-end">
        <PencilIcon />
      </IconButton>
    </div>
  );
};

export default ClaimMilestoneRow;
